import React, { useEffect, useMemo, useState } from "react";
import { Input } from "antd";
import { Icon } from "Components";
import InfinityScrollWrapper from "Components/InfinityScroll/InfinityScrollWrapper";
import { DEFAULT_CAMPAIGNS_PAGESIZE } from "PhishingManager/PhishingManager.constants";
import PropertySelect from "../CampaignSteps/components/PropertySelect";
import RecipientItem from "../CampaignSteps/components/RecipientItem";
import SelectedLabel from "./SelectedLabel";

interface RoleOption {
    key: string;
    label: string;
    count: number;
    description: string;
}

interface PropertyRole {
    key: string;
    count: number;
}

interface Props {
    propertyOptions: any[];
    roleOptions: RoleOption[];
    propertyRoles: Record<string, PropertyRole[]>;

    selectedRoles: string[];
    setSelectedRoles: (roles: string[]) => void;

    roleSearchQuery: string;
    setRoleSearchQuery: (value: string) => void;
}

const RoleRecipients: React.FC<Props> = ({
    propertyOptions,
    roleOptions,
    propertyRoles,
    selectedRoles,
    setSelectedRoles,
    roleSearchQuery,
    setRoleSearchQuery,
}) => {
    const [selectedProperties, setSelectedProperties] = useState<string[]>([]);
    const [allSelected, setAllSelected] = useState(false);
    const [rolesPage, setRolesPage] = useState(1);
    const [visibleRoles, setVisibleRoles] = useState<(RoleOption & { count: number })[]>([]);
    const [hasMoreRoles, setHasMoreRoles] = useState(false);
    const [rolesLoading, setRolesLoading] = useState(false);

    const allPropertyRoles = useMemo(() => roleOptions.map((role) => {
        const totalCount = Object.entries(propertyRoles)
            .filter(([key]) => key.toLowerCase() !== "all")
            .reduce((acc, [_, roles]) => {
                const match = roles.find((r) => r.key === role.key);
                return acc + (match?.count ?? 0);
            }, 0);
        return { ...role, count: totalCount };
    }), [roleOptions, propertyRoles]);

    const availableRoles = useMemo(() => {
        if (!allSelected && selectedProperties.length === 0) return [];

        const roles = allSelected
            ? allPropertyRoles
            : selectedProperties
                .flatMap((propKey) =>
                    (propertyRoles[propKey] ?? []).map(({ key, count }: PropertyRole) => {
                        const base = roleOptions.find((r) => r.key === key)!;
                        return { ...base, count };
                    })
                )
                .reduce((acc, role) => {
                    // Merge duplicate roles across properties by summing counts
                    const existing = acc.find((r) => r.key === role.key);
                    if (existing) {
                        existing.count += role.count;
                    } else {
                        acc.push({ ...role });
                    }
                    return acc;
                }, [] as (RoleOption & { count: number })[]);

        return roles.filter(
            (r) =>
                r.label.toLowerCase().includes(roleSearchQuery.toLowerCase()) ||
                r.description.toLowerCase().includes(roleSearchQuery.toLowerCase())
        );
    }, [selectedProperties, allSelected, allPropertyRoles, propertyRoles, roleOptions, roleSearchQuery]);

    const allRolesSelected =
        availableRoles.length > 0 &&
        availableRoles.every((r) => selectedRoles.includes(r.key));

    const totalRoleCount = availableRoles
        .filter((r) => selectedRoles.includes(r.key))
        .reduce((acc, r) => acc + r.count, 0);

    const handleSelectAll = () => {
        if (allRolesSelected) {
            setSelectedRoles([]);
        } else {
            setSelectedRoles(availableRoles.map((r) => r.key));
        }
    };

    const handleRoleToggle = (key: string) => {
        if (selectedRoles.includes(key)) {
            setSelectedRoles(selectedRoles.filter((r) => r !== key));
        } else {
            setSelectedRoles([...selectedRoles, key]);
        }
    };

    const loadMoreRoles = () => {
        setRolesLoading(true);
        setTimeout(() => {
            const nextPage = rolesPage + 1;
            const nextItems = availableRoles.slice(0, nextPage * DEFAULT_CAMPAIGNS_PAGESIZE);
            setVisibleRoles(nextItems);
            setRolesPage(nextPage);
            setHasMoreRoles(nextItems.length < availableRoles.length);
            setRolesLoading(false);
        }, 1000);
    };

    useEffect(() => {
        const firstPage = availableRoles.slice(0, DEFAULT_CAMPAIGNS_PAGESIZE);
        setVisibleRoles(firstPage);
        setRolesPage(1);
        setHasMoreRoles(availableRoles.length > DEFAULT_CAMPAIGNS_PAGESIZE);
    }, [availableRoles]);

    useEffect(() => {
        if (!allSelected) {
            setSelectedRoles([]);
        }
    }, [allSelected]);

    return (
        <div className="phishing-step3__role-section">
            <div className="phishing-step3__role-filters">
                <div className="phishing-step3__filter-group">
                    <div className="phishing-step3__label">
                        Select property <span className="required">*</span>
                    </div>
                    <PropertySelect
                        propertyOptions={propertyOptions}
                        selectedProperties={selectedProperties}
                        setSelectedProperties={setSelectedProperties}
                        allSelected={allSelected}
                        setAllSelected={setAllSelected}
                        onPropertyDeselect={(propertyValue) => {
                            // Remove roles that belong only to the deselected property
                            const rolesForProperty = (propertyRoles[propertyValue] ?? []).map((r) => r.key);
                            const remainingProperties = selectedProperties.filter((p) => p !== propertyValue);
                            const rolesStillValid = new Set(
                                remainingProperties.flatMap((p) =>
                                    (propertyRoles[p] ?? []).map((r) => r.key)
                                )
                            );
                            setSelectedRoles(
                                selectedRoles.filter(
                                    (roleKey) =>
                                        !rolesForProperty.includes(roleKey) ||
                                        rolesStillValid.has(roleKey)
                                )
                            );
                        }}
                    />
                </div>

                <div className="phishing-step3__filter-group phishing-step3__role-search">
                    <div>
                        <Input.Search
                            placeholder="Search by role name"
                            value={roleSearchQuery}
                            onChange={(e: any) => setRoleSearchQuery(e.target.value)}
                            suffix={<Icon type="search" />}
                        />
                    </div>
                </div>
            </div>

            <SelectedLabel labelText={`Select a role ${availableRoles.length > 0 ? `(${availableRoles.length} Results)` : ''}`} totalCount={totalRoleCount} selectedAll={allRolesSelected} handleSelectAll={handleSelectAll} />

            <div className="phishing-step3__role-list">
                {selectedProperties.length === 0 && !allSelected ? (
                    <div className="phishing-step3__role-item phishing-step3__role-item--empty">
                        Please select a property to see roles.
                    </div>
                ) : availableRoles.length === 0 ? (
                    <div className="phishing-step3__role-item phishing-step3__role-item--empty">
                        No roles found.
                    </div>
                ) : (
                    <InfinityScrollWrapper
                        dataLength={visibleRoles.length}
                        hasMore={hasMoreRoles}
                        endMessageText=""
                        onLoadMore={loadMoreRoles}
                        loading={rolesLoading}
                        height={400}
                        scrollableId="role-list-infinite-scroll"
                        loaderText="Loading roles..."
                    >
                        {visibleRoles.map((role) => (
                            <RecipientItem
                                key={role.key}
                                title={role.label}
                                description={`${role.description} - (${role.count}) users`}
                                iconType="recipientRole"
                                isChecked={selectedRoles.includes(role.key)}
                                onToggle={() => handleRoleToggle(role.key)}
                            />
                        ))}
                    </InfinityScrollWrapper>
                )}
            </div>
        </div>
    );
};

export default RoleRecipients;